import type { FC, ReactNode } from 'react'
import type { Staff } from '@/types'

import { useEffect, useId, useState } from 'react'
import { Camera, ImagePlus, Trash2 } from 'lucide-react'
import { Avatar } from '@/components/kit/avatar'
import { Button } from '@/components/kit/button'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { Field, Input } from '@/components/kit/field'
import { usePhotoCapture } from '@/components/entry/use-photo-capture'
import { useCarWashActions } from '@/hooks/use-carwash-actions'
import { AVATAR_PHOTO_SIZE } from '@/lib/carwash/image'
import { formatPhone, isValidPhone } from '@/lib/carwash/phone'
import { deletePhoto, isLocalPhoto, savePhoto } from '@/lib/carwash/data/photo-store'

interface StaffFormSheetProps {
    staff: Staff | null
    open: boolean
    onClose: () => void
    onSaved?: (staffId: string) => void
}

/* Add or edit a staff member: a name, and optionally a phone number and photo. No logins. */
export const StaffFormSheet: FC<StaffFormSheetProps> = ({ staff, open, onClose, onSaved }): ReactNode => {
    const actions = useCarWashActions()
    const toast = useToast()
    const nameId = useId()
    const phoneId = useId()

    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [photo, setPhoto] = useState<string | null>(null)
    const [photoChanged, setPhotoChanged] = useState(false)
    const [touched, setTouched] = useState(false)
    const [saving, setSaving] = useState(false)

    const capture = usePhotoCapture({
        maxSize: AVATAR_PHOTO_SIZE,
        onPhoto: dataUrl => {
            setPhoto(dataUrl)
            setPhotoChanged(true)
        }
    })

    useEffect(() => {
        if (!open) return

        setName(staff?.name ?? '')
        setPhone(staff?.phone ? formatPhone(staff.phone) : '')
        setPhoto(staff?.photo ?? null)
        setPhotoChanged(false)
        setTouched(false)
        // Fill the form when it opens only.
    }, [open, staff?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    const nameError = touched && !name.trim() ? 'Enter a name.' : null
    const phoneError = phone.trim() && !isValidPhone(phone) ? 'Enter a valid mobile number, or leave it empty.' : null

    const save = async () => {
        setTouched(true)
        if (!name.trim() || phoneError) return

        setSaving(true)

        let photoRef = staff?.photo ?? null

        if (photoChanged) {
            photoRef = photo ? await savePhoto(photo) : null
        }

        const input = { name: name.trim(), phone: phone.trim() || null, photo: photoRef }
        let savedId: string | null = null

        if (staff) {
            if (actions.updateStaff(staff.id, input)) savedId = staff.id
        }
        else {
            savedId = actions.createStaff(input)
        }

        setSaving(false)

        if (!savedId) {
            if (photoChanged && photoRef && isLocalPhoto(photoRef)) void deletePhoto(photoRef)
            return
        }

        if (photoChanged && staff?.photo && staff.photo !== photoRef) void deletePhoto(staff.photo)

        toast({ title: staff ? `${input.name} updated` : `${input.name} added to staff`, description: staff ? undefined : 'They are available for the next vehicle.' })
        onSaved?.(savedId)
        onClose()
    }

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title={staff ? 'Edit staff member' : 'Add staff member'}
            description='Staff do not need an account. Only you use this app.'
            size='sm'
            footer={(
                <Button size='lg' className='w-full' onClick={save} loading={saving}>
                    {staff ? 'Save changes' : 'Add staff member'}
                </Button>
            )}
        >
            {capture.elements}

            <div className='space-y-5'>
                <div className='flex items-center gap-4'>
                    <Avatar name={name || '?'} photo={photo} size='xl' />

                    <div className='flex flex-wrap gap-2'>
                        <Button size='sm' variant='secondary' onClick={capture.openCamera} loading={capture.busy}>
                            <Camera aria-hidden='true' />
                            {photo ? 'Retake' : 'Take photo'}
                        </Button>

                        <Button size='sm' variant='secondary' onClick={capture.openGallery}>
                            <ImagePlus aria-hidden='true' />
                            Upload
                        </Button>

                        {photo && (
                            <Button
                                size='sm'
                                variant='ghost'
                                onClick={() => {
                                    setPhoto(null)
                                    setPhotoChanged(true)
                                }}
                            >
                                <Trash2 aria-hidden='true' />
                                Remove
                            </Button>
                        )}
                    </div>
                </div>

                <Field label='Name' htmlFor={nameId} error={nameError}>
                    <Input
                        id={nameId}
                        autoComplete='off'
                        autoCapitalize='words'
                        placeholder='e.g. Rahman'
                        value={name}
                        maxLength={40}
                        onChange={event => setName(event.target.value)}
                        aria-invalid={Boolean(nameError)}
                    />
                </Field>

                <Field label='Phone number' htmlFor={phoneId} optional error={phoneError}>
                    <Input
                        id={phoneId}
                        type='tel'
                        inputMode='tel'
                        autoComplete='off'
                        placeholder='98470 12345'
                        value={phone}
                        onChange={event => setPhone(event.target.value)}
                        aria-invalid={Boolean(phoneError)}
                    />
                </Field>
            </div>
        </Sheet>
    )
}
